import { getDataOrNull } from "@msys/common";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  Alert,
  AlertTitle,
  Button,
  FormHelperText,
  TextField as MuiTextField,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { debounce } from "lodash-es";
import React from "react";
import {
  OnboardingFlowModal_OrganisationDataPrefillQuery,
  useOnboardingFlowModal_OrganisationDataPrefillLazyQuery,
} from "./OnboardingFlowModal.generated.js";
import { websiteUrlPrefillValidationSchema } from "./websiteUrl.js";

export interface OnboardingWebsitePrefillData {
  website: string;
  prefill: Extract<
    OnboardingFlowModal_OrganisationDataPrefillQuery["organisationDataPrefill"],
    { __typename: "OrganisationDataPrefill" }
  >;
}

interface Props {
  organisationName: string;
  /** The organisation's current website; shown in the box on first render. */
  initialWebsite: string;
  /** Called on every edit so the form's website follows the box. */
  onWebsiteChange: (website: string) => void;
  onDataFound: (data: OnboardingWebsitePrefillData) => Promise<void>;
  onLoadingChange: (loading: boolean) => void;
  disabled?: boolean;
}

export const OnboardingFlowWebsitePrefill = ({
  organisationName,
  initialWebsite,
  onWebsiteChange,
  onDataFound,
  onLoadingChange,
  disabled = false,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);

  const [website, setWebsite] = React.useState(initialWebsite);
  const [websiteValidationError, setWebsiteValidationError] =
    React.useState<Error | null>(null);
  const [searchError, setSearchError] = React.useState<Error | null>(null);
  // Spans the whole click handler (validation, query, awaited onDataFound) —
  // unlike the lazy query's own loading flag, which only covers the network
  // request and would let a second click start an overlapping lookup.
  const [prefillInFlight, setPrefillInFlight] = React.useState(false);
  const [search] = useOnboardingFlowModal_OrganisationDataPrefillLazyQuery();

  const validateWebsite = React.useCallback(
    async (value: string): Promise<string | null> => {
      // An empty input is a neutral state, not a validation failure.
      if (!value.trim()) {
        setWebsiteValidationError(null);
        return null;
      }
      try {
        const normalizedWebsite = await websiteUrlPrefillValidationSchema()
          .required()
          .validate(value);
        setWebsiteValidationError(null);
        return normalizedWebsite;
      } catch (error) {
        const validationError =
          error instanceof Error ? error : new Error(String(error));
        // Keep the previous Error instance while the message is unchanged so
        // React bails out of the state update instead of re-rendering on
        // every keystroke that repeats the same error.
        setWebsiteValidationError(previous =>
          previous?.message === validationError.message
            ? previous
            : validationError
        );
        return null;
      }
    },
    []
  );

  const debouncedValidateWebsite = React.useMemo(
    () => debounce(validateWebsite, 300, { leading: false, trailing: true }),
    [validateWebsite]
  );
  React.useEffect(
    () => () => debouncedValidateWebsite.cancel(),
    [debouncedValidateWebsite]
  );

  const handleFillInData = async () => {
    if (disabled || prefillInFlight || !website.trim()) return;

    setPrefillInFlight(true);
    try {
      debouncedValidateWebsite.cancel();
      const normalizedWebsite = await validateWebsite(website);
      if (!normalizedWebsite) {
        return;
      }

      setSearchError(null);
      onLoadingChange(true);
      try {
        const { data, error } = await search({
          variables: {
            input: {
              organisationName,
              website: normalizedWebsite,
            },
          },
        });

        const prefill = getDataOrNull(data?.organisationDataPrefill);
        if (error || !prefill) {
          setSearchError(
            error ??
              new Error(
                t("Something went wrong. Please try again.", { ns: "Global" })
              )
          );
          return;
        }

        await onDataFound({
          website: normalizedWebsite,
          prefill,
        });
      } finally {
        onLoadingChange(false);
      }
    } finally {
      setPrefillInFlight(false);
    }
  };

  return (
    <Alert
      variant="outlined"
      icon={false}
      sx={{
        color: "#96319D",
        backgroundColor: "#FCFAFC",
        borderColor: "#FFE2FF",
        ".MuiAlert-message": {
          flex: 1,
        },
      }}
      action={
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleFillInData}
          disabled={disabled || Boolean(websiteValidationError)}
          loading={prefillInFlight}
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("Fill in data", { ns: "OrganisationProfile" })}
        </Button>
      }
    >
      <AlertTitle>
        {t("Fill company data with AI", {
          ns: "OrganisationProfile",
        })}
      </AlertTitle>
      <MuiTextField
        label="Website"
        // Editing mid-lookup would let the settled response write a website
        // into the form that no longer matches the visible input.
        disabled={prefillInFlight}
        onChange={event => {
          setWebsite(event.target.value);
          onWebsiteChange(event.target.value);
          setSearchError(null);
          debouncedValidateWebsite(event.target.value);
        }}
        error={Boolean(websiteValidationError)}
        helperText={
          websiteValidationError ? websiteValidationError.message : ""
        }
        value={website}
      />
      {searchError && (
        <FormHelperText error>{searchError.message}</FormHelperText>
      )}
    </Alert>
  );
};
